<script setup>
import { ref, reactive, onBeforeMount, onMounted, onBeforeUnmount, computed } from "vue";
import { useStore } from "vuex";
import api from '@/services/api.js';
import Swal from 'sweetalert2';
import logo from "../assets/img/Login/logofondooscuro.png";

// Importaciones de Argon Dashboard
import setNavPills from "@/assets/js/nav-pills.js";
import setTooltip from "@/assets/js/tooltip.js";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";

const body = document.getElementsByTagName("body")[0];
const store = useStore();

const user = ref(null);
const isEditing = ref(false);

const editableUser = ref({
  first_name: '',
  last_name: '',
  email: '',
  telefono: '',
  ci: '',
  username: '',
  password: '',
  // NUEVO: Campos para la cascada orgánica
  regional: '',
  gerencia: '',
  seccion: '',
  cargo: ''
});

// NUEVO: Listas para la cascada
const listas = reactive({
  regionales: [],
  gerencias: [],
  secciones: [],
  cargos: []
});

// --- Validaciones ---
const isPasswordValid = computed(() => {
  if (!editableUser.value.password) return true;
  return editableUser.value.password.length >= 8;
});

const canSave = computed(() => {
  return isPasswordValid.value &&
    editableUser.value.first_name.trim() !== '' &&
    editableUser.value.username.trim() !== '';
});

// --- Funciones de Cascada (Carga de Datos) ---
const cargarRegionales = async () => {
  try {
    const res = await api.get('regionales/');
    listas.regionales = res.data.results || res.data;
  } catch (e) { console.error("Error cargando regionales", e); }
};

const fetchGerencias = async (cambioManual = false) => {
  if (cambioManual) {
    editableUser.value.gerencia = '';
    editableUser.value.seccion = '';
    editableUser.value.cargo = '';
    listas.secciones = [];
    listas.cargos = [];
  }
  if (!editableUser.value.regional) return;
  try {
    const res = await api.get(`gerencias/?regional=${editableUser.value.regional}`);
    listas.gerencias = res.data.results || res.data;
  } catch (e) { console.error(e); }
};

const fetchSecciones = async (cambioManual = false) => {
  if (cambioManual) {
    editableUser.value.seccion = '';
    editableUser.value.cargo = '';
    listas.cargos = [];
  }
  if (!editableUser.value.gerencia) return;
  try {
    const res = await api.get(`secciones/?gerencia=${editableUser.value.gerencia}`);
    listas.secciones = res.data.results || res.data;
  } catch (e) { console.error(e); }
};

const fetchCargos = async (cambioManual = false) => {
  if (cambioManual) {
    editableUser.value.cargo = '';
  }
  if (!editableUser.value.seccion) return;
  try {
    const res = await api.get(`cargos/?seccion=${editableUser.value.seccion}`);
    listas.cargos = res.data.results || res.data;
  } catch (e) { console.error(e); }
};

// --- Acciones de Perfil ---
const fetchUserProfile = async () => {
  try {
    const response = await api.get('me/');
    user.value = response.data;

    // Guardamos en LocalStorage para otros componentes
    localStorage.setItem('user_rol', response.data.rol || 'USUARIO_FINAL');

    resetForm();

    // Cargar listas iniciales para que el usuario vea su jerarquía pre-seleccionada
    if (editableUser.value.regional) await fetchGerencias();
    if (editableUser.value.gerencia) await fetchSecciones();
    if (editableUser.value.seccion) await fetchCargos();

  } catch (error) {
    console.error("Error al obtener perfil", error);
  }
};

const resetForm = () => {
  editableUser.value = {
    first_name: user.value.first_name || '',
    last_name: user.value.last_name || '',
    email: user.value.email || '',
    ci: user.value.ci || '',
    telefono: user.value.telefono || '',
    username: user.value.username || '',
    password: '',
    regional: user.value.regional_id || user.value.regional || '',
    gerencia: user.value.gerencia_id || user.value.gerencia || '',
    seccion: user.value.seccion_id || user.value.seccion || '',
    cargo: user.value.cargo_id || user.value.cargo || ''
  };
};

const saveProfile = async () => {
  if (!canSave.value) return;

  const result = await Swal.fire({
    title: '¿Confirmar cambios?',
    text: "Se actualizará su información en el sistema de Activos Fijos.",
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#2dce89',
    cancelButtonColor: '#f5365c',
    confirmButtonText: 'Sí, guardar',
    cancelButtonText: 'Cancelar'
  });

  if (result.isConfirmed) {
    const dataToSave = { ...editableUser.value };
    if (!dataToSave.password) delete dataToSave.password;

    try {
      const response = await api.patch('me/', dataToSave);
      user.value = response.data;

      // Sincronización Local
      localStorage.setItem("first_name", response.data.first_name);
      localStorage.setItem("last_name", response.data.last_name);
      localStorage.setItem("username", response.data.username);

      Swal.fire({
        title: '¡Actualizado!',
        text: 'Su perfil ha sido modificado con éxito.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      isEditing.value = false;
      editableUser.value.password = '';
    } catch (error) {
      console.error(error);
    }
  }
};

const toggleEditMode = () => {
  isEditing.value = !isEditing.value;
  if (!isEditing.value && user.value) resetForm();
};

// Formatear el nombre del rol
const formatRole = (rolStr) => {
  if (!rolStr) return 'Funcionario';
  const rolesMap = {
    'ADMIN_NACIONAL': 'Jefe Nacional de Activos',
    'ADMIN_REGIONAL': 'Jefe Regional de Activos',
    'USUARIO_FINAL': 'Usuario Final'
  };
  return rolesMap[rolStr] || rolStr;
};

onMounted(() => {
  store.state.isAbsolute = true;
  setNavPills();
  setTooltip();
  cargarRegionales();
  fetchUserProfile();
});

onBeforeMount(() => {
  store.state.imageLayout = "profile-overview";
  store.state.showNavbar = false;
  store.state.showFooter = true;
  store.state.hideConfigButton = true;
  body.classList.add("profile-overview");
});

onBeforeUnmount(() => {
  store.state.isAbsolute = false;
  store.state.imageLayout = "default";
  store.state.showNavbar = true;
  body.classList.remove("profile-overview");
});
</script>

<template>
  <main class="app-transition">
    <div class="container-fluid">
      <div class="page-header min-height-300 border-radius-xl mt-0"
        style="background-image: url('https://www.tamep.bo/assets/png/1-Ds-k_scH.png');">
        <span class="mask bg-tamep opacity-6"></span>
      </div>

      <div class="card card-custom shadow-lg mx-4 mt-n6">
        <div class="card-body p-3">
          <div class="row gx-4">
            <div class="col-auto">
              <div class="avatar avatar-xl position-relative bg-white p-1 border-radius-lg shadow-sm">
                <img :src="logo" class="w-100 border-radius-lg" style="object-fit: contain;" />
              </div>
            </div>
            <div class="col-auto my-auto">
              <div class="h-100">
                <h5 class="mb-1 text-title-custom">{{ user ? (user.first_name + ' ' + user.last_name) : 'Cargando...' }}
                </h5>
                <p class="mb-0 font-weight-bold text-sm text-primary">
                  {{ user ? formatRole(user.rol) : 'Funcionario' }}
                </p>
                <p class="mb-0 text-xs text-custom-muted">
                  <i class="fas fa-map-marker-alt me-1"></i> {{ user?.regional_nombre || 'Sede TAMEP' }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="py-4 container-fluid">
      <div class="row">
        <div class="col-md-8">
          <div class="card card-custom mb-4 shadow-lg border-0 border-radius-xl overflow-hidden">
            <div class="header-tamep d-flex align-items-center px-4 py-3 border-radius-top">
              <h6 class="mb-0 text-white font-weight-bolder text-uppercase">Gestión de Perfil e Identidad</h6>
              <div class="ms-auto d-flex gap-2">
                <argon-button :color="isEditing ? 'danger' : 'warning'" size="sm" class="mb-0 font-weight-bolder py-2" @click="toggleEditMode">
                  <i class="fas" :class="isEditing ? 'fa-times' : 'fa-edit'"></i>
                  {{ isEditing ? ' Cancelar' : ' Editar' }}
                </argon-button>
                <argon-button v-if="isEditing" color="success" size="sm" class="mb-0 shadow-sm font-weight-bolder py-2" :disabled="!canSave" @click="saveProfile">
                  <i class="fas fa-save"></i>
                  {{ !isPasswordValid ? ' Clave Corta' : ' Guardar' }}
                </argon-button>
              </div>
            </div>

            <div class="card-body p-4 bg-section-custom">
              <p class="text-uppercase text-xs font-weight-bolder opacity-7 text-primary mb-3">Información Personal</p>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-control-label text-xs text-custom-muted">Nombres</label>
                  <argon-input v-model="editableUser.first_name" :disabled="!isEditing" />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-control-label text-xs text-custom-muted">Apellidos</label>
                  <argon-input v-model="editableUser.last_name" :disabled="!isEditing" />
                </div>

                <div class="col-md-4 mb-3">
                  <label class="form-control-label text-xs text-custom-muted">Cédula de Identidad (CI)</label>
                  <argon-input v-model="editableUser.ci" :disabled="!isEditing" placeholder="Ej: 1234567" />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-control-label text-xs text-custom-muted">Teléfono / Celular</label>
                  <argon-input v-model="editableUser.telefono" :disabled="!isEditing" placeholder="Ej: 71234567" />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-control-label text-xs text-custom-muted">Correo Electrónico</label>
                  <argon-input type="email" v-model="editableUser.email" :disabled="!isEditing"
                    placeholder="usuario@tamep.bo" />
                </div>
              </div>

              <hr class="horizontal border-custom-divider my-4" />
              <p class="text-uppercase text-xs font-weight-bolder opacity-7 text-primary mb-3">Ubicación Orgánica e
                Institucional</p>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-control-label text-xs text-custom-muted">Regional</label>
                  <select class="form-control form-select select-custom" v-model="editableUser.regional"
                    :disabled="!isEditing" @change="fetchGerencias(true)">
                    <option value="">Seleccione Regional...</option>
                    <option v-for="r in listas.regionales" :key="r.id" :value="r.id">{{ r.nombre }}</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-control-label text-xs text-custom-muted">Gerencia</label>
                  <select class="form-control form-select select-custom" v-model="editableUser.gerencia"
                    :disabled="!isEditing || !editableUser.regional" @change="fetchSecciones(true)">
                    <option value="">Seleccione Gerencia...</option>
                    <option v-for="g in listas.gerencias" :key="g.id" :value="g.id">{{ g.nombre }}</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-control-label text-xs text-info">Sección / Área</label>
                  <select class="form-control form-select select-custom border-info-light"
                    v-model="editableUser.seccion" :disabled="!isEditing || !editableUser.gerencia"
                    @change="fetchCargos(true)">
                    <option value="">Seleccione Sección...</option>
                    <option v-for="s in listas.secciones" :key="s.id" :value="s.id">{{ s.nombre }}</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-control-label text-xs text-custom-muted">Cargo Operativo</label>
                  <select class="form-control form-select select-custom" v-model="editableUser.cargo"
                    :disabled="!isEditing || !editableUser.seccion">
                    <option value="">Seleccione Cargo...</option>
                    <option v-for="c in listas.cargos" :key="c.id" :value="c.id">{{ c.nombre }}</option>
                  </select>
                </div>
              </div>

              <hr class="horizontal border-custom-divider my-4" />
              <p class="text-uppercase text-xs font-weight-bolder opacity-7 text-primary mb-3">Seguridad de Acceso</p>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-control-label text-xs text-custom-muted">Nombre de Usuario (Login)</label>
                  <argon-input v-model="editableUser.username" :disabled="!isEditing" />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-control-label text-xs text-danger">Cambiar Contraseña (Opcional)</label>
                  <argon-input type="password" v-model="editableUser.password" :disabled="!isEditing"
                    placeholder="Solo si desea cambiarla (Mín. 8 caracteres)" />
                  <p v-if="editableUser.password && !isPasswordValid"
                    class="text-danger text-xxs mt-1 mb-0 font-weight-bold">
                    <i class="fas fa-exclamation-triangle me-1"></i> Mínimo 8 caracteres requeridos.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card card-custom h-100 shadow-lg border-0 border-radius-xl overflow-hidden">
            <div class="header-tamep px-4 py-3 border-radius-top text-center text-white">
              <h6 class="mb-0 text-white font-weight-bolder text-uppercase">Visión Corporativa</h6>
            </div>
            <div class="card-body text-center p-4 bg-section-custom border-radius-bottom">
              <div class="icon-box-modal bg-gradient-primary text-white shadow-sm mx-auto mb-3" style="width: 52px; height: 52px;">
                <i class="fas text-white fa-lg"
                  :class="user?.rol === 'ADMIN_NACIONAL' ? 'fa-shield-alt' : (user?.rol === 'ADMIN_REGIONAL' ? 'fa-map-marked-alt' : 'fa-user')"></i>
              </div>

              <h5 class="mt-2 text-title-custom font-weight-bolder">Transporte Aéreo Militar</h5>
              <p class="text-xs px-2 text-custom-muted mb-3">
                Plataforma de Control de Activos Fijos (SIGAF).
              </p>

              <div class="d-flex justify-content-center mb-3">
                <span class="badge bg-gradient-success text-white px-3 py-2 text-xs border-radius-md shadow-sm">
                  <i class="fas fa-check-circle me-1"></i> Cuenta Verificada
                </span>
              </div>

              <hr class="horizontal border-custom-divider mb-3" />

              <div class="text-start">
                <h6 class="text-xs text-uppercase font-weight-bolder text-custom-muted mb-3">Detalles de la Sesión</h6>

                <div class="d-flex align-items-center mb-3 p-2 rounded bg-switch-container border border-custom-divider">
                  <div class="icon-box-badge bg-warning text-dark me-3 shadow-sm">
                    <i class="fas fa-key text-xs"></i>
                  </div>
                  <div>
                    <h6 class="mb-0 text-xs font-weight-bold text-title-custom">Autenticación Segura</h6>
                    <p class="text-xxs text-custom-muted mb-0">Protegida vía Token JWT</p>
                  </div>
                </div>

                <div class="d-flex align-items-center p-2 rounded bg-switch-container border border-custom-divider">
                  <div class="icon-box-badge bg-info text-white me-3 shadow-sm">
                    <i class="fas fa-id-card-alt text-xs"></i>
                  </div>
                  <div>
                    <h6 class="mb-0 text-xs font-weight-bold text-title-custom">Nivel de Acceso</h6>
                    <p class="text-xxs text-custom-muted mb-0">{{ user ? formatRole(user.rol) : 'Cargando...' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
/* Transiciones Globales */
.app-transition {
  transition: all 0.3s ease;
}

.bg-tamep {
  background-color: #003366 !important;
}

/* Variables Claras */
.header-tamep { background: linear-gradient(135deg, #003366 0%, #001d3d 100%); }
.border-radius-top { border-top-left-radius: 12px; border-top-right-radius: 12px; }
.border-radius-bottom { border-bottom-left-radius: 12px; border-bottom-right-radius: 12px; }
.bg-section-custom { background-color: #f8fafc; }

.card-custom {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.card-header-custom {
  background-color: #ffffff;
  transition: background-color 0.3s ease;
  border-bottom: none;
}

.text-title-custom {
  color: #1e293b;
  transition: color 0.3s ease;
}

.text-custom-muted {
  color: #64748b;
  transition: color 0.3s ease;
}

.border-custom-divider {
  border-color: #e2e8f0 !important;
  transition: border-color 0.3s ease;
}

.select-custom {
  background-color: #ffffff;
  color: #1e293b;
  border-color: #cbd5e1;
  transition: all 0.3s ease;
}

.border-info-light {
  border-color: #38bdf8 !important;
}

.bg-switch-container {
  background-color: #ffffff;
}

/* Icon Box Clases */
.icon-box-modal {
  width: 52px; height: 52px; min-width: 52px; min-height: 52px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%; flex-shrink: 0;
}
.icon-box-badge {
  width: 32px; height: 32px; min-width: 32px; min-height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%; flex-shrink: 0;
}

/* Mejora visual para botones y selectores deshabilitados (LIGHT MODE) */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

select:disabled,
input:disabled,
.form-control:disabled {
  background-color: #f1f5f9 !important;
  color: #1e293b !important;
  border-color: #cbd5e1 !important;
  opacity: 1 !important;
  cursor: not-allowed;
}

.avatar-xl img {
  object-fit: contain;
  background-color: white;
  padding: 5px;
}
</style>

<style>
/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom, .dark-mode .card-custom {
  background-color: #1e293b !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5) !important;
  border: 1px solid #334155 !important;
}

.dark-version .card-header-custom, .dark-mode .card-header-custom {
  background-color: #1e293b !important;
}

.dark-version .border-custom-divider, .dark-mode .border-custom-divider {
  border-color: #334155 !important;
}

.dark-version .bg-section-custom, .dark-mode .bg-section-custom {
  background-color: #090d16 !important;
}

.dark-version .bg-switch-container, .dark-mode .bg-switch-container {
  background-color: #0f172a !important;
}

.dark-version .text-title-custom, .dark-mode .text-title-custom {
  color: #ffffff !important;
}

.dark-version .text-custom-muted, .dark-mode .text-custom-muted {
  color: #cbd5e1 !important;
}

/* Inputs y Selects en Modo Oscuro (Activos) */
.dark-version .form-control, .dark-mode .form-control,
.dark-version .select-custom, .dark-mode .select-custom {
  background-color: #0f172a !important;
  color: #f8fafc !important;
  border-color: #334155 !important;
}

.dark-version .form-control:focus, .dark-mode .form-control:focus,
.dark-version .select-custom:focus, .dark-mode .select-custom:focus {
  border-color: #38bdf8 !important;
  box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.2) !important;
}

/* Inputs y Selects en Modo Oscuro (Deshabilitados - Lectura Clara) */
.dark-version .form-control:disabled, .dark-mode .form-control:disabled,
.dark-version select:disabled, .dark-mode select:disabled {
  background-color: #0f172a !important;
  color: #f8fafc !important;
  border-color: #1e293b !important;
  opacity: 0.9 !important;
}

/* Options dentro de SELECT en Modo Oscuro */
.dark-version select option, .dark-mode select option {
  background-color: #0f172a !important;
  color: #f8fafc !important;
}

/* Avatar en Modo Oscuro */
.dark-version .avatar-xl img, .dark-mode .avatar-xl img {
  background-color: #ffffff !important;
  border: 1px solid #334155;
}

/* Título de color primario adaptado para fondos oscuros */
.dark-version .text-primary, .dark-mode .text-primary {
  color: #38bdf8 !important;
}
</style>